import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import SendIcon from '@mui/icons-material/Send';
import { stripFootnoteNumberForSoaExport } from '../../utils/soaTableExportModel';
import documentEditorService from '../../services/documentEditor.service';
import soaEditService from '../../services/soaEdit.service';

/** Same algorithm as SoaTableRenderer.jsx's buildColumnKey — must match exactly, since
 * grid marks are looked up by this joined key. Never derive it from headerTier text. */
function buildColumnKey(col) {
  return [col?.l1, col?.l2, col?.l3, col?.l4]
    .map((p) => (p == null || p === '' ? '' : String(p).trim()))
    .filter((p) => p.length > 0)
    .join('|');
}

function emptyTableScaffold() {
  return {
    tableId: 'T1',
    label: '',
    scope: '',
    inheritsFromTableId: null,
    layeredColumnHeaders: true,
    columns: [],
    rowGroups: [],
    grid: [],
    footnotes: [],
  };
}

/** Upgrades the flat legacy {visits, sections, footnotes} shape (a catalogue with no rich
 * soaGrid extension, or a table saved by an earlier flat-only version of this editor) into a
 * single-tier tiered table, so it can be edited (and will stay tiered) going forward. */
function legacyFlatToTieredTable(flat) {
  const visits = Array.isArray(flat?.visits) ? flat.visits : [];
  const sections = Array.isArray(flat?.sections) ? flat.sections : [];
  const flatFootnotes = Array.isArray(flat?.footnotes) ? flat.footnotes : [];

  const columns = visits.map((v, idx) => {
    const period = String(v?.period || '').trim() || `Visit ${idx + 1}`;
    const day = String(v?.day || '').trim();
    return {
      l1: period, l2: day || null, l3: null, l4: null, window: null,
      headerTier1: period, headerTier2: day, headerTier3: '',
    };
  });
  const columnKeys = columns.map((c) => buildColumnKey(c));

  const footnotes = flatFootnotes.map((text, idx) => ({
    id: String(idx + 1),
    text: stripFootnoteNumberForSoaExport(text),
  }));

  const rowGroups = sections.map((section) => ({
    group: section?.title || '',
    assessments: (section?.activities || []).map((a) => {
      const refs = a?.power && footnotes[a.power - 1] ? [footnotes[a.power - 1].id] : [];
      return { name: a?.name || '', status: 'confirmed', ...(refs.length ? { footnoteRefs: refs } : {}) };
    }),
  }));

  const grid = [];
  sections.forEach((section) => {
    (section?.activities || []).forEach((a) => {
      const marks = (a?.visits || [])
        .map((idx) => columnKeys[idx])
        .filter(Boolean)
        .map((columnKey) => ({ columnKey, value: 'X' }));
      if (marks.length) grid.push({ assessment: a?.name || '', marks });
    });
  });

  return { ...emptyTableScaffold(), columns, rowGroups, grid, footnotes };
}

/** Tiered table -> the Excel-like grid's {header, rows} editing view. Visit-column header
 * entries are {group, subgroup, label} objects (not strings) so the compact 3-tier label can
 * be edited without losing structure; the two fixed columns stay plain strings. */
function tableToExcelData(table) {
  const columns = Array.isArray(table?.columns) ? table.columns : [];
  const rowGroups = Array.isArray(table?.rowGroups) ? table.rowGroups : [];
  const grid = Array.isArray(table?.grid) ? table.grid : [];
  const footnotes = Array.isArray(table?.footnotes) ? table.footnotes : [];
  const footnoteTextById = new Map(
    footnotes.map((f) => [String(f?.id ?? ''), stripFootnoteNumberForSoaExport(f?.text || '')])
  );

  const columnKeys = columns.map((c) => buildColumnKey(c));
  const header = [
    'Study procedures and visits',
    ...columns.map((c) => ({
      group: String(c?.headerTier1 ?? c?.l1 ?? '').trim(),
      subgroup: String(c?.headerTier2 ?? c?.l2 ?? '').trim(),
      label: String(c?.headerTier3 ?? c?.window ?? c?.l3 ?? '').trim(),
    })),
    'Key Notes',
  ];

  const markLookup = new Map();
  grid.forEach((row) => {
    const name = String(row?.assessment || '').trim();
    if (!name) return;
    const byCol = new Map();
    (row?.marks || []).forEach((m) => {
      const ck = String(m?.columnKey || '').trim();
      if (ck) byCol.set(ck, m?.value ?? '');
    });
    markLookup.set(name, byCol);
  });

  const rows = [];
  rowGroups.forEach((group, groupIndex) => {
    rows.push({
      id: `group-${groupIndex}-${rows.length}`,
      type: 'group',
      data: [group?.group || '', ...Array(header.length - 1).fill('')],
    });
    (group?.assessments || []).forEach((a, aIndex) => {
      const name = typeof a === 'string' ? a : String(a?.name || '').trim();
      const marksForRow = markLookup.get(name) || new Map();
      const refs = Array.isArray(a?.footnoteRefs) ? a.footnoteRefs : a?.footnoteRef ? [a.footnoteRef] : [];
      const keynotesValue = refs.map((id) => footnoteTextById.get(String(id))).filter(Boolean).join('\n');
      rows.push({
        id: `assessment-${groupIndex}-${aIndex}-${rows.length}`,
        type: 'assessment',
        group: group?.group || '',
        data: [
          name,
          ...columnKeys.map((ck) => {
            const v = marksForRow.get(ck);
            return v ? String(v) : '';
          }),
          keynotesValue,
        ],
      });
    });
  });

  return { header, rows };
}

/** Inverse of tableToExcelData — rebuilds columns/rowGroups/grid/footnotes fresh from the
 * current grid state each time, so a renamed column's columnKey and its marks always stay in
 * sync (no separate "migrate old key" step needed: the grid is rebuilt positionally from the
 * live header + rows, not patched against the previous table). */
function excelDataToTable(excelData, previousFootnotes) {
  const header = excelData.header;
  const keynotesColumnIndex = header.length - 1;
  const columnHeaders = header.slice(1, keynotesColumnIndex);

  const columns = columnHeaders.map((h) => {
    const group = String(h?.group || '').trim();
    const subgroup = String(h?.subgroup || '').trim();
    const label = String(h?.label || '').trim();
    return {
      l1: group || null,
      l2: subgroup || null,
      l3: label || null,
      l4: null,
      window: label || null,
      headerTier1: group,
      headerTier2: subgroup,
      headerTier3: label,
    };
  });
  const columnKeys = columns.map((c) => buildColumnKey(c));

  let rowGroups = [];
  let currentGroup = null;
  let footnotes = [...(previousFootnotes || [])];
  const gridByAssessment = new Map();

  excelData.rows.forEach((row) => {
    if (row.type === 'group') {
      if (currentGroup) rowGroups.push(currentGroup);
      currentGroup = { group: row.data[0], assessments: [] };
    } else if (row.type === 'assessment' && currentGroup) {
      const name = row.data[0];
      const keynotesValue = stripFootnoteNumberForSoaExport((row.data[keynotesColumnIndex] || '').trim());
      let footnoteRefs;
      if (keynotesValue) {
        const existing = footnotes.find((fn) => fn?.text === keynotesValue);
        const id = existing ? existing.id : String(footnotes.length + 1);
        if (!existing) footnotes.push({ id, text: keynotesValue });
        footnoteRefs = [id];
      }
      currentGroup.assessments.push({
        name,
        status: 'confirmed',
        ...(footnoteRefs ? { footnoteRefs } : {}),
      });

      const marks = new Map();
      row.data.slice(1, keynotesColumnIndex).forEach((cell, idx) => {
        const v = String(cell || '').trim();
        if (v) marks.set(columnKeys[idx], v);
      });
      gridByAssessment.set(name, marks);
    }
  });
  if (currentGroup) rowGroups.push(currentGroup);

  const grid = [...gridByAssessment.entries()].map(([assessment, marks]) => ({
    assessment,
    marks: [...marks.entries()].map(([columnKey, value]) => ({ columnKey, value })),
  }));

  return { columns, rowGroups, grid, footnotes };
}

const EditSoaDialog = ({ open, onClose, sectionId, projectId, generatedSoAData, initialChatHistory, onSaved }) => {
  const [excelData, setExcelData] = useState({ header: [], rows: [] });
  const [footnotesBaseline, setFootnotesBaseline] = useState([]);
  const [otherTables, setOtherTables] = useState([]);
  const [baseTableMeta, setBaseTableMeta] = useState(emptyTableScaffold());

  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');

  const [addColOpen, setAddColOpen] = useState(false);
  const [newColGroup, setNewColGroup] = useState('');
  const [newColSubgroup, setNewColSubgroup] = useState('');
  const [newColLabel, setNewColLabel] = useState('');
  const [insertAfterCol, setInsertAfterCol] = useState(-1);
  const [colPosition, setColPosition] = useState('end');

  const [addRowOpen, setAddRowOpen] = useState(false);
  const [newRowType, setNewRowType] = useState('assessment');
  const [newRowName, setNewRowName] = useState('');
  const [insertAfterRow, setInsertAfterRow] = useState(-1);
  const [rowPosition, setRowPosition] = useState('end');

  const [editColumnDialogOpen, setEditColumnDialogOpen] = useState(false);
  const [editingColumnIndex, setEditingColumnIndex] = useState(null);
  const [editColumnGroup, setEditColumnGroup] = useState('');
  const [editColumnSubgroup, setEditColumnSubgroup] = useState('');
  const [editColumnLabel, setEditColumnLabel] = useState('');

  const [keynotesDialogOpen, setKeynotesDialogOpen] = useState(false);
  const [editingKeynotesRowIndex, setEditingKeynotesRowIndex] = useState(null);
  const [editingKeynotesActivityName, setEditingKeynotesActivityName] = useState('');
  const [keynotesEditValue, setKeynotesEditValue] = useState('');

  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [chatError, setChatError] = useState('');
  const chatScrollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let primary;
    let siblings = [];
    if (Array.isArray(generatedSoAData?.tables) && generatedSoAData.tables.length) {
      const tables = generatedSoAData.tables;
      primary = tables.find((t) => !t?.inheritsFromTableId) || tables[0];
      siblings = tables.filter((t) => t !== primary);
    } else if (Array.isArray(generatedSoAData?.visits) || Array.isArray(generatedSoAData?.sections)) {
      primary = legacyFlatToTieredTable(generatedSoAData);
    } else {
      primary = emptyTableScaffold();
    }
    setOtherTables(siblings);
    setBaseTableMeta({
      tableId: primary.tableId || 'T1',
      label: primary.label || '',
      scope: primary.scope || '',
      inheritsFromTableId: primary.inheritsFromTableId ?? null,
      layeredColumnHeaders: primary.layeredColumnHeaders !== false,
    });
    setFootnotesBaseline(Array.isArray(primary.footnotes) ? primary.footnotes : []);
    setExcelData(tableToExcelData(primary));
    setMessages(Array.isArray(initialChatHistory) ? initialChatHistory : []);
    setDraft('');
    setChatError('');
    setSaveError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const el = chatScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, chatBusy]);

  const handleCellClick = (rowIndex, colIndex) => {
    if (editingCell && editingCell.rowIndex === rowIndex && editingCell.colIndex === colIndex) return;
    const isKeynotesColumn = colIndex === excelData.header.length - 1;
    const row = excelData.rows[rowIndex];

    if (row.type === 'group' && colIndex === 0) {
      setEditingCell({ rowIndex, colIndex });
      setEditValue(row.data[colIndex]);
    } else if (row.type === 'assessment') {
      if (colIndex === 0) {
        setEditingCell({ rowIndex, colIndex });
        setEditValue(row.data[colIndex]);
      } else if (isKeynotesColumn) {
        setEditingKeynotesRowIndex(rowIndex);
        setEditingKeynotesActivityName(row.data[0] || '');
        setKeynotesEditValue(row.data[colIndex] || '');
        setKeynotesDialogOpen(true);
      } else if (colIndex > 0 && colIndex < excelData.header.length - 1) {
        setExcelData((prev) => {
          const next = { ...prev, rows: prev.rows.map((r) => ({ ...r, data: [...r.data] })) };
          const current = next.rows[rowIndex].data[colIndex];
          next.rows[rowIndex].data[colIndex] = current === 'X' ? '' : 'X';
          return next;
        });
      }
    }
  };

  const handleEditSave = () => {
    if (!editingCell) return;
    setExcelData((prev) => {
      const next = { ...prev, rows: prev.rows.map((r) => ({ ...r, data: [...r.data] })) };
      next.rows[editingCell.rowIndex].data[editingCell.colIndex] = editValue;
      return next;
    });
    setEditingCell(null);
    setEditValue('');
  };

  const handleEditCancel = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleKeynotesDialogClose = () => {
    setKeynotesDialogOpen(false);
    setEditingKeynotesRowIndex(null);
    setEditingKeynotesActivityName('');
    setKeynotesEditValue('');
  };

  const handleKeynotesDialogSave = () => {
    if (editingKeynotesRowIndex !== null) {
      setExcelData((prev) => {
        const next = { ...prev, rows: prev.rows.map((r) => ({ ...r, data: [...r.data] })) };
        const keynotesColumnIndex = prev.header.length - 1;
        next.rows[editingKeynotesRowIndex].data[keynotesColumnIndex] = keynotesEditValue;
        return next;
      });
    }
    handleKeynotesDialogClose();
  };

  const handleColumnEdit = (colIndex) => {
    if (colIndex === 0) return;
    setEditingColumnIndex(colIndex);
    const col = excelData.header[colIndex] || {};
    setEditColumnGroup(col.group || '');
    setEditColumnSubgroup(col.subgroup || '');
    setEditColumnLabel(col.label || '');
    setEditColumnDialogOpen(true);
  };

  const handleColumnDialogClose = () => {
    setEditColumnDialogOpen(false);
    setEditingColumnIndex(null);
    setEditColumnGroup('');
    setEditColumnSubgroup('');
    setEditColumnLabel('');
  };

  const handleColumnSave = () => {
    if (editingColumnIndex !== null && (editColumnGroup.trim() || editColumnLabel.trim())) {
      setExcelData((prev) => {
        const next = { ...prev, header: [...prev.header] };
        next.header[editingColumnIndex] = {
          group: editColumnGroup.trim(),
          subgroup: editColumnSubgroup.trim(),
          label: editColumnLabel.trim(),
        };
        return next;
      });
    }
    handleColumnDialogClose();
  };

  const handleDeleteColumn = (colIndex) => {
    if (colIndex === 0) return;
    setExcelData((prev) => ({
      header: prev.header.filter((_, idx) => idx !== colIndex),
      rows: prev.rows.map((row) => ({ ...row, data: row.data.filter((_, idx) => idx !== colIndex) })),
    }));
  };

  const handleAddColumnConfirm = () => {
    if (!newColGroup.trim() && !newColLabel.trim()) return;
    setExcelData((prev) => {
      let insertIndex;
      switch (colPosition) {
        case 'beginning':
          insertIndex = 1;
          break;
        case 'after':
          insertIndex = insertAfterCol + 1;
          break;
        case 'before':
          insertIndex = insertAfterCol;
          break;
        case 'end':
        default:
          insertIndex = prev.header.length - 1;
      }
      const newCol = { group: newColGroup.trim(), subgroup: newColSubgroup.trim(), label: newColLabel.trim() };
      return {
        header: [...prev.header.slice(0, insertIndex), newCol, ...prev.header.slice(insertIndex)],
        rows: prev.rows.map((row) => ({
          ...row,
          data: [...row.data.slice(0, insertIndex), '', ...row.data.slice(insertIndex)],
        })),
      };
    });
    setAddColOpen(false);
    setNewColGroup('');
    setNewColSubgroup('');
    setNewColLabel('');
    setInsertAfterCol(-1);
    setColPosition('end');
  };

  const handleAddRowConfirm = () => {
    if (!newRowName.trim()) return;
    setExcelData((prev) => {
      let insertIndex;
      switch (rowPosition) {
        case 'beginning':
          insertIndex = 0;
          break;
        case 'after':
          insertIndex = insertAfterRow + 1;
          break;
        case 'before':
          insertIndex = insertAfterRow;
          break;
        case 'end':
        default:
          insertIndex = prev.rows.length;
      }

      if (newRowType === 'group') {
        const newGroup = {
          id: `group-new-${Date.now()}`,
          type: 'group',
          data: [newRowName, ...Array(prev.header.length - 1).fill('')],
        };
        return { ...prev, rows: [...prev.rows.slice(0, insertIndex), newGroup, ...prev.rows.slice(insertIndex)] };
      }

      let targetGroup = null;
      let groupIndex = -1;
      for (let i = insertIndex - 1; i >= 0; i -= 1) {
        if (prev.rows[i].type === 'group') {
          targetGroup = prev.rows[i];
          groupIndex = i;
          break;
        }
      }
      if (!targetGroup) {
        for (let i = insertIndex; i < prev.rows.length; i += 1) {
          if (prev.rows[i].type === 'group') {
            targetGroup = prev.rows[i];
            groupIndex = i;
            break;
          }
        }
      }
      if (!targetGroup) {
        const newGroup = {
          id: `group-new-${Date.now()}`,
          type: 'group',
          data: ['New Group', ...Array(prev.header.length - 1).fill('')],
        };
        const newAssessment = {
          id: `assessment-new-${Date.now()}`,
          type: 'assessment',
          group: 'New Group',
          data: [newRowName, ...Array(prev.header.length - 1).fill('')],
        };
        return {
          ...prev,
          rows: [...prev.rows.slice(0, insertIndex), newGroup, newAssessment, ...prev.rows.slice(insertIndex)],
        };
      }
      const newAssessment = {
        id: `assessment-${groupIndex}-${Date.now()}`,
        type: 'assessment',
        group: targetGroup.data[0],
        data: [newRowName, ...Array(prev.header.length - 2).fill(''), ''],
      };
      const insertPos = insertIndex > groupIndex ? insertIndex : groupIndex + 1;
      return { ...prev, rows: [...prev.rows.slice(0, insertPos), newAssessment, ...prev.rows.slice(insertPos)] };
    });
    setAddRowOpen(false);
    setNewRowName('');
    setNewRowType('assessment');
    setInsertAfterRow(-1);
    setRowPosition('end');
  };

  const handleSave = async () => {
    setSaveLoading(true);
    setSaveError('');
    try {
      const rebuilt = excelDataToTable(excelData, footnotesBaseline);
      const primaryTable = { ...baseTableMeta, ...rebuilt };
      const tables = [primaryTable, ...otherTables];
      await documentEditorService.updateSection(
        sectionId,
        { scheduleOfActivitiesData: { tables }, soaEditChatHistory: messages },
        projectId
      );
      setFootnotesBaseline(rebuilt.footnotes);
      setSaveSuccess(true);
      if (onSaved) onSaved({ tables }, messages);
    } catch (err) {
      setSaveError(err?.message || 'Failed to save Schedule of Activities.');
    } finally {
      setSaveLoading(false);
    }
  };

  const sendChatMessage = async (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed || chatBusy) return;
    setChatBusy(true);
    setChatError('');
    try {
      const userMsg = { id: `user-${Date.now()}`, role: 'user', rawContent: trimmed };
      const nextMessages = [...messages, userMsg];
      const rebuilt = excelDataToTable(excelData, footnotesBaseline);
      const tableForChat = { ...baseTableMeta, ...rebuilt };

      const res = await soaEditService.sendSoaEditMessage({ table: tableForChat, messages: nextMessages });

      const assistantMsg = { id: `assistant-${Date.now()}`, role: 'assistant', rawContent: res.assistantReply || '' };
      setMessages([...nextMessages, assistantMsg]);
      setBaseTableMeta({
        tableId: res.table?.tableId || tableForChat.tableId,
        label: res.table?.label ?? tableForChat.label,
        scope: res.table?.scope ?? tableForChat.scope,
        inheritsFromTableId: res.table?.inheritsFromTableId ?? tableForChat.inheritsFromTableId,
        layeredColumnHeaders: res.table?.layeredColumnHeaders !== false,
      });
      setExcelData(tableToExcelData(res.table));
      setFootnotesBaseline(res.table?.footnotes || []);
      setDraft('');
    } catch (err) {
      setChatError(err?.message || 'SOA edit request failed.');
    } finally {
      setChatBusy(false);
    }
  };

  // Column widths as percentages (not fixed px) so the whole table always fits the dialog's
  // width without horizontal scrolling — label/Key Notes get a fixed share, visit columns
  // split what's left evenly, however many there are. Merged Group/Sub-group header cells
  // (below) mean each individual column only ever has to show its short Label horizontally,
  // so this stays readable even with many narrow columns — no rotation needed.
  const LABEL_COL_PCT = 16;
  const KEYNOTES_COL_PCT = 14;
  const visitColCount = Math.max(0, excelData.header.length - 2);
  const visitColPct = visitColCount > 0 ? (100 - LABEL_COL_PCT - KEYNOTES_COL_PCT) / visitColCount : 0;
  const colWidthPct = (colIdx) => {
    if (colIdx === 0) return LABEL_COL_PCT;
    if (colIdx === excelData.header.length - 1) return KEYNOTES_COL_PCT;
    return visitColPct;
  };

  const getCellStyle = (rowIndex, colIndex, value) => {
    const isKeynotesCol = colIndex === excelData.header.length - 1;
    const baseStyle = {
      border: '1px solid #ddd',
      padding: '8px',
      cursor: 'pointer',
      overflowWrap: 'break-word',
      whiteSpace: isKeynotesCol ? 'normal' : undefined,
      textAlign: colIndex === 0 || isKeynotesCol ? 'left' : 'center',
    };
    if (excelData.rows[rowIndex]?.type === 'group') {
      return { ...baseStyle, backgroundColor: '#f0f0f0', fontWeight: 'bold', textAlign: 'left' };
    }
    if (colIndex === 0) return { ...baseStyle, textAlign: 'left', fontWeight: 500 };
    if (value === 'X') return { ...baseStyle, backgroundColor: '#e8f5e8', fontWeight: 'bold', color: '#2e7d32' };
    return baseStyle;
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle>Edit Schedule of Activities</DialogTitle>
      <DialogContent
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '320px minmax(0, 1fr)' },
          gap: 2,
          pt: 2,
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {/* Chat pane — edits an already-populated table, never generates one from scratch */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Edit with chat
            </Typography>
          </Box>
          <Box ref={chatScrollRef} sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 1.5, bgcolor: 'grey.50' }}>
            {messages.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                Ask for a change, e.g. &quot;add a Day 15 column under Cycle 1&quot; or &quot;remove the ECG row&quot;.
              </Typography>
            ) : (
              <Stack spacing={1.25}>
                {messages.map((m) => (
                  <Box
                    key={m.id}
                    sx={{
                      alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                      maxWidth: '92%',
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: m.role === 'user' ? 'primary.main' : 'background.paper',
                      color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
                      border: m.role === 'user' ? 'none' : '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {m.rawContent}
                    </Typography>
                  </Box>
                ))}
                {chatBusy && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 0.5 }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">
                      Applying edit…
                    </Typography>
                  </Stack>
                )}
              </Stack>
            )}
          </Box>
          {chatError && (
            <Alert severity="error" onClose={() => setChatError('')} sx={{ mx: 1.5, mb: 1 }}>
              {chatError}
            </Alert>
          )}
          <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={1} alignItems="flex-end">
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                size="small"
                placeholder="Describe the change…"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendChatMessage(draft);
                  }
                }}
                disabled={chatBusy}
              />
              <IconButton color="primary" onClick={() => sendChatMessage(draft)} disabled={chatBusy || !draft.trim()}>
                {chatBusy ? <CircularProgress size={18} /> : <SendIcon fontSize="small" />}
              </IconButton>
            </Stack>
          </Box>
        </Box>

        {/* Manual grid pane */}
        <Box sx={{ minWidth: 0, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAddRowOpen(true)}>
              Add Row
            </Button>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAddColOpen(true)}>
              Add Column
            </Button>
          </Stack>

          {excelData.header?.length ? (
            <TableContainer sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <colgroup>
                    {excelData.header.map((_, colIdx) => (
                      <col key={colIdx} style={{ width: `${colWidthPct(colIdx)}%` }} />
                    ))}
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      {excelData.header.map((col, colIdx) => {
                        const isLabelCol = colIdx === 0;
                        const isKeynotesCol = colIdx === excelData.header.length - 1;
                        const isVisitCol = !isLabelCol && !isKeynotesCol;
                        const colGroup = isVisitCol ? String(col?.group || '') : '';
                        const colSubgroup = isVisitCol ? String(col?.subgroup || '') : '';
                        const colLabel = isVisitCol ? String(col?.label || '') : '';
                        return (
                          <TableCell key={colIdx} sx={{ verticalAlign: 'top', py: 0.75 }}>
                            {isVisitCol ? (
                              <Stack spacing={0}>
                                <Stack direction="row" justifyContent="center" sx={{ flexShrink: 0 }}>
                                  <Tooltip title="Edit column">
                                    <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => { e.stopPropagation(); handleColumnEdit(colIdx); }}>
                                      <EditIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete column">
                                    <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => { e.stopPropagation(); handleDeleteColumn(colIdx); }}>
                                      <DeleteIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                                {colGroup && (
                                  <Typography variant="body2" align="center" sx={{ fontWeight: 600, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    {colGroup}
                                  </Typography>
                                )}
                                {colSubgroup && (
                                  <Typography variant="caption" align="center" sx={{ display: 'block', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    {colSubgroup}
                                  </Typography>
                                )}
                                <Typography variant="caption" align="center" color="text.secondary" sx={{ display: 'block', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  {colLabel}
                                </Typography>
                              </Stack>
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {col}
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {excelData.rows.map((row, rowIndex) => (
                      <TableRow key={row.id}>
                        {row.data.map((cell, colIndex) => (
                          <TableCell key={colIndex} sx={getCellStyle(rowIndex, colIndex, cell)} onClick={() => handleCellClick(rowIndex, colIndex)}>
                            {editingCell?.rowIndex === rowIndex && editingCell?.colIndex === colIndex ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  size="small"
                                  autoFocus
                                />
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditSave(); }} color="primary">
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditCancel(); }} color="error">
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ) : (
                              cell
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No Schedule of Activities data to edit yet.
              </Typography>
            )}
        </Box>
      </DialogContent>

      {saveError && (
        <Alert severity="error" onClose={() => setSaveError('')} sx={{ mx: 3 }}>
          {saveError}
        </Alert>
      )}

      <DialogActions>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={saveLoading} startIcon={<SaveIcon />}>
          {saveLoading ? 'Saving…' : 'Save'}
        </Button>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>

      {/* Add Column dialog */}
      <Dialog open={addColOpen} onClose={() => setAddColOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Column</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Group (e.g. Treatment Cycles)"
              value={newColGroup}
              onChange={(e) => setNewColGroup(e.target.value)}
              placeholder="e.g., Screening, Treatment Cycles"
              autoFocus
            />
            <TextField
              fullWidth
              label="Sub-group (e.g. Cycle 1 (28 days)) — optional"
              value={newColSubgroup}
              onChange={(e) => setNewColSubgroup(e.target.value)}
              placeholder="Leave blank if this column doesn't belong to a sub-group"
            />
            <TextField
              fullWidth
              label="Label (e.g. Day 1)"
              value={newColLabel}
              onChange={(e) => setNewColLabel(e.target.value)}
              placeholder="e.g., Day 1, Day 15, V1"
            />
            <Typography variant="caption" color="text.secondary">
              Reuse the exact same Group/Sub-group text as a neighboring column to merge them under one shared header.
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Position</InputLabel>
              <Select value={colPosition} label="Position" onChange={(e) => setColPosition(e.target.value)}>
                <MenuItem value="beginning">At the beginning</MenuItem>
                <MenuItem value="end">At the end</MenuItem>
                <MenuItem value="after">After a column</MenuItem>
                <MenuItem value="before">Before a column</MenuItem>
              </Select>
            </FormControl>
            {(colPosition === 'after' || colPosition === 'before') && (
              <FormControl fullWidth>
                <InputLabel>Select Column</InputLabel>
                <Select value={insertAfterCol} label="Select Column" onChange={(e) => setInsertAfterCol(e.target.value)}>
                  {excelData.header.slice(1, excelData.header.length - 1).map((col, index) => (
                    <MenuItem key={index} value={index + 1}>
                      {[col?.group, col?.subgroup, col?.label].filter(Boolean).join(' / ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddColOpen(false)}>Cancel</Button>
          <Button onClick={handleAddColumnConfirm} variant="contained">Add Column</Button>
        </DialogActions>
      </Dialog>

      {/* Add Row dialog */}
      <Dialog open={addRowOpen} onClose={() => setAddRowOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Row</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Row Type</InputLabel>
              <Select value={newRowType} label="Row Type" onChange={(e) => setNewRowType(e.target.value)}>
                <MenuItem value="group">Group (Header)</MenuItem>
                <MenuItem value="assessment">Assessment (Under a group)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Row Name"
              value={newRowName}
              onChange={(e) => setNewRowName(e.target.value)}
              placeholder={newRowType === 'group' ? 'e.g., Safety Assessments' : 'e.g., Physical Examination'}
              autoFocus
            />
            <FormControl fullWidth>
              <InputLabel>Position</InputLabel>
              <Select value={rowPosition} label="Position" onChange={(e) => setRowPosition(e.target.value)}>
                <MenuItem value="beginning">At the beginning</MenuItem>
                <MenuItem value="end">At the end</MenuItem>
                <MenuItem value="after">After a row</MenuItem>
                <MenuItem value="before">Before a row</MenuItem>
              </Select>
            </FormControl>
            {(rowPosition === 'after' || rowPosition === 'before') && (
              <FormControl fullWidth>
                <InputLabel>Select Row</InputLabel>
                <Select value={insertAfterRow} label="Select Row" onChange={(e) => setInsertAfterRow(e.target.value)}>
                  {excelData.rows.map((row, index) => (
                    <MenuItem key={index} value={index}>
                      {row.data[0]} ({row.type})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddRowOpen(false)}>Cancel</Button>
          <Button onClick={handleAddRowConfirm} variant="contained">Add Row</Button>
        </DialogActions>
      </Dialog>

      {/* Column header edit dialog */}
      <Dialog open={editColumnDialogOpen} onClose={handleColumnDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Column</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Group (e.g. Treatment Cycles)"
            fullWidth
            value={editColumnGroup}
            onChange={(e) => setEditColumnGroup(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label="Sub-group (e.g. Cycle 1 (28 days)) — optional"
            fullWidth
            value={editColumnSubgroup}
            onChange={(e) => setEditColumnSubgroup(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label="Label (e.g. Day 1)"
            fullWidth
            value={editColumnLabel}
            onChange={(e) => setEditColumnLabel(e.target.value)}
            sx={{ mt: 2 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Reuse the exact same Group/Sub-group text as a neighboring column to merge them under one shared header.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleColumnDialogClose}>Cancel</Button>
          <Button onClick={handleColumnSave} variant="contained" disabled={!editColumnGroup.trim() && !editColumnLabel.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Keynotes edit dialog */}
      <Dialog open={keynotesDialogOpen} onClose={handleKeynotesDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>Edit Key Notes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Assessment: <strong>{editingKeynotesActivityName}</strong>
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={6}
            maxRows={12}
            label="Keynotes (Footnotes)"
            value={keynotesEditValue}
            onChange={(e) => setKeynotesEditValue(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleKeynotesDialogClose}>Cancel</Button>
          <Button onClick={handleKeynotesDialogSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={saveSuccess} autoHideDuration={3000} onClose={() => setSaveSuccess(false)}>
        <Alert onClose={() => setSaveSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Schedule of Activities saved.
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default EditSoaDialog;
